// src/padplanner/PlanReview.jsx
//
// The review half of the plan page (S6a2, mockup 5): the stepper (in the strip since 2026-10-31), Submit,
// the approval route, the chain, the decision box, Issue, the versions strip,
// and comments. Start revision is in the plan page's head (PlanPage.jsx). The rules of what is offered are
// planLifecycle.js's; every write is S6a1's, which refuses what it will not
// do, and a refusal is shown in its words with its code.
//
// Submit is here and in the editor's head: one component.
import { useEffect, useState } from "react";
import { SectionLabel } from "./SidePanel";
import ReadFailed from "./ReadFailed";
import {
  getChecks, submitPlan, decideStep, issuePlan, listRoutes, updatePlan, addComment, editComment, resolveComment,
} from "../api/padplannerApi";
import { refusalText } from "./planRefusals";
import { PLAN_TYPE_LABELS } from "./planVocabulary";
import { verdictOf } from "./checksVocabulary";
import { RerunChecks } from "./ChecksTab";
import {
  stepperState, STEP_STATE_WORDS, submitState, chainRows, versionRows, splitVersions, shortDate, CHAIN_STATE_WORDS, NOTE_MIN, NOTE_MAX,
} from "./planLifecycle";

const field = "w-full rounded border border-bg-border bg-bg px-2 py-1.5 text-[13px] text-fg-primary";

/** A refusal, in its words and with its code. */
export function Refusal({ error, testid }) {
  if (!error) return null;
  return (
    <p className="text-[12.5px] text-accent-red" role="alert" data-testid={testid}>
      {refusalText(error)} {(error.code || error.status) && <span className="op-mono">({error.code ?? error.status})</span>}
    </p>
  );
}

/* ── the stepper ───────────────────────────────────────────────────────── */

const DOT = {
  done: "border-status-ok bg-status-ok/20 text-status-ok",
  current: "border-[rgb(var(--warn))] bg-[rgb(var(--warn))]/20 text-[rgb(var(--warn-text))]",
  attention: "border-accent-red bg-accent-red/15 text-accent-red",
  todo: "border-bg-border text-fg-secondary",
};

/**
 * The stepper as one line, for the PadPlanner strip's right end (2026-10-31):
 * the same states and colors, dots and labels smaller. Below 1100 px the labels
 * collapse to dots, but for the step the version is at; every step's title
 * says what it is. The strip's height does not change (the dots are 16 px).
 */
export function CompactStepper({ status }) {
  const steps = stepperState(status);
  const at = steps.findLastIndex((s) => s.state !== "todo");
  return (
    <ol className="flex items-center gap-1.5" aria-label="Plan lifecycle" data-testid="pp-stepper" data-status={status}>
      {steps.map((s, i) => (
        <li key={s.key} className="flex items-center gap-1.5" data-step={s.key} data-state={s.state}
          title={`${s.label}: ${STEP_STATE_WORDS[s.state]}`}
          aria-current={s.state === "current" || s.state === "attention" ? "step" : undefined}>
          {i > 0 && <span aria-hidden="true" className={`h-px w-3 ${s.state === "todo" ? "bg-bg-border" : "bg-status-ok/60"}`} />}
          <span aria-hidden="true" className={`flex h-4 w-4 items-center justify-center rounded-full border text-[9px] font-semibold ${DOT[s.state]}`}>
            {s.state === "done" ? "✓" : i + 1}
          </span>
          <span data-testid="pp-step-label" data-at={i === at ? "true" : undefined}
            className={`whitespace-nowrap text-[10.5px] uppercase tracking-wide ${i === at ? "" : "sr-only min-[1100px]:not-sr-only"} ${s.state === "todo" ? "text-fg-secondary" : "text-fg-primary"}`}>
            {s.label}
          </span>
        </li>
      ))}
    </ol>
  );
}

/* ── Submit ────────────────────────────────────────────────────────────── */

/**
 * Submit for review. The token is read fresh from the checks at the click
 * (S6a1: submit takes the checks' revision.token), so the revision submitted
 * is the one whose verdict was just read; a 409 says it moved.
 */
export function SubmitButton({ planId, planNumber, approval, checks, canEdit, dirty = false, onSubmitted, className = "", approvalError = null, onRetry = null, onRerun = null, rerun = undefined }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const s = submitState({ approval, checks, canEdit, dirty, planNumber });
  // Without the approval record whether it can be submitted is unknown: said, not hidden.
  if (!approval && approvalError && canEdit) {
    return <ReadFailed what="whether this plan can be submitted" onRetry={onRetry} testid="pp-submit-unknown" className={className} />;
  }
  if (!s.show) return null;

  async function submit() {
    if (!window.confirm(s.confirm)) return;
    setBusy(true);
    setError(null);
    try {
      const fresh = await getChecks(planId);
      const r = await submitPlan(planId, fresh.revision.token);
      onSubmitted?.(r);
    } catch (err) { setError(err); } finally { setBusy(false); }
  }

  return (
    <div className={`flex flex-col gap-1 ${className}`} data-testid="pp-submit">
      <button type="button" className="op-btn op-btn--primary op-btn--sm self-start" disabled={!s.enabled || busy} onClick={submit}
        data-testid="pp-submit-button">
        {busy ? "Submitting…" : "Submit for review"}
      </button>
      {s.reason && <p className="op-meta" data-testid="pp-submit-reason">{s.reason}</p>}
      {/* Out of date: the re-run is offered here, not only in the Checks tab. */}
      <RerunChecks onRerun={verdictOf(checks ?? {}) === "stale" ? onRerun : null} rerun={rerun} testid="pp-submit-rerun" />
      <Refusal error={error} testid="pp-submit-error" />
    </div>
  );
}

/* ── the route ─────────────────────────────────────────────────────────── */

/** The route submit would use, and while the plan is a draft, a choice of route. */
export function RouteRow({ plan, approval, canEdit, onChanged }) {
  const [routes, setRoutes] = useState(null);
  const [routesError, setRoutesError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState(null);
  const choosable = canEdit && approval?.status === "draft" && !plan.revision.frozen;
  useEffect(() => {
    if (!choosable) return undefined;
    let cancelled = false;
    // A failure is said with a retry, not offered as a choice of the default alone.
    listRoutes().then((r) => { if (!cancelled) { setRoutes(r.routes ?? []); setRoutesError(null); } })
      .catch((e) => { if (!cancelled) { setRoutes(null); setRoutesError(e); } });
    return () => { cancelled = true; };
  }, [choosable, attempt]);
  const route = approval?.route ?? null;
  const fits = (routes ?? []).filter((r) => r.plan_type === null || r.plan_type === plan.plan_type);

  async function choose(id) {
    setError(null);
    try { await updatePlan(plan.id, { route_id: id || null }); onChanged?.(); } catch (err) { setError(err); }
  }

  return (
    <div className="flex flex-col gap-1" data-testid="pp-route">
      <SectionLabel>Approval route</SectionLabel>
      <p className="text-[13px] text-fg-primary" data-testid="pp-route-name">
        {route ? route.name : "None"}
        {route && <span className="op-meta"> · {route.chosen ? "chosen for this plan" : `default for ${route.plan_type ? PLAN_TYPE_LABELS[route.plan_type] ?? route.plan_type : "any type"}`}</span>}
      </p>
      {choosable && routes && (
        <select className={field} aria-label="Approval route" value={plan.route?.id ?? ""} onChange={(e) => choose(e.target.value)}>
          <option value="">The organization's default</option>
          {fits.map((r) => <option key={r.id} value={r.id}>{r.name}{r.is_default ? " (default)" : ""}</option>)}
        </select>
      )}
      {choosable && routesError && <ReadFailed what="the approval routes" error={routesError} onRetry={() => setAttempt((n) => n + 1)} testid="pp-route-list-failed" />}
      <Refusal error={error} testid="pp-route-error" />
    </div>
  );
}

/* ── the chain and the decision ────────────────────────────────────────── */

const CHAIN_TONE = {
  approved: "text-status-ok", changes_requested: "text-accent-red", waiting: "text-[rgb(var(--warn-text))]", next: "text-fg-secondary", skipped: "text-fg-secondary",
};

/** Mockup 5: role · approver, and on the right what it did and when, or waiting / next. */
export function ApprovalChain({ revision }) {
  const rows = chainRows(revision);
  if (!rows.length) return null;
  return (
    <div className="flex flex-col gap-1.5" data-testid="pp-chain">
      <SectionLabel>Approvals · v{revision.rev_no}{revision.route?.name ? ` · ${revision.route.name}` : ""}</SectionLabel>
      <ul className="flex flex-col gap-1.5">
        {rows.map((r) => (
          <li key={r.ordinal} className="flex flex-col gap-0.5 text-[13px]" data-testid="pp-chain-step" data-state={r.state}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="min-w-0 text-fg-primary">{r.role} · <span className="text-fg-secondary">{r.by ?? r.approver}</span></span>
              <span className={`op-mono shrink-0 text-[11.5px] ${CHAIN_TONE[r.state]}`}>
                {CHAIN_STATE_WORDS[r.state]}{r.at ? ` ${shortDate(r.at)}` : ""}
              </span>
            </div>
            {r.note && <p className="op-meta pl-2" data-testid="pp-chain-note">“{r.note}”</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Approve, or Request changes with a note of at least 10 characters (trimmed): the server's rule, and its refusal if not. */
export function DecisionBox({ planId, box, onDecided }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const trimmed = note.trim().length;

  async function decide(decision) {
    setBusy(true);
    setError(null);
    try {
      const r = await decideStep(planId, box.ordinal, decision, note.trim() ? note : null);
      setNote("");
      onDecided?.(r);
    } catch (err) { setError(err); } finally { setBusy(false); }
  }

  return (
    <section className="flex flex-col gap-2" aria-label="Your decision" data-testid="pp-decision">
      <SectionLabel>Your decision · {box.role_label}</SectionLabel>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className="op-btn op-btn--sm border-status-ok/50 bg-status-ok/15 text-status-ok" disabled={busy}
          onClick={() => decide("approved")}>Approve</button>
        <button type="button" className="op-btn op-btn--sm border-accent-red/50 bg-accent-red/10 text-accent-red" disabled={busy}
          onClick={() => decide("changes_requested")}>Request changes</button>
      </div>
      <label className="flex flex-col gap-1">
        <span className="op-label uppercase">Note</span>
        <textarea className={field} rows={2} maxLength={NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Required to request changes" aria-label="Decision note" />
        <span className="op-meta" data-testid="pp-note-count">{trimmed < NOTE_MIN ? `${trimmed}/${NOTE_MIN} characters to request changes` : "Ready"}</span>
      </label>
      <Refusal error={error} testid="pp-decision-error" />
    </section>
  );
}

/* ── issue ─────────────────────────────────────────────────────────────── */

// Start revision was here; it is in the plan page's head (headActions), where
// an issued plan's editor finds it without opening the Review tab.
export function LifecycleActions({ planId, approval, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const can = approval?.can ?? {};
  // What Issue did stays said after the button is gone.
  if (!can.issue) {
    if (!done && !error) return null;
    return (
      <div className="flex flex-col gap-1.5">
        {done && <p className="op-meta" role="status">{done}</p>}
        <Refusal error={error} testid="pp-lifecycle-error" />
      </div>
    );
  }

  async function run(fn, said) {
    setBusy(true);
    setError(null);
    try { const r = await fn(); setDone(said(r)); onDone?.(r); } catch (err) { setError(err); } finally { setBusy(false); }
  }

  return (
    <div className="flex flex-col gap-1.5" data-testid="pp-lifecycle-actions">
      <div className="flex flex-wrap gap-2">
        {can.issue && (
          <button type="button" className="op-btn op-btn--primary op-btn--sm" disabled={busy} data-testid="pp-issue"
            onClick={() => window.confirm(`Issue v${approval.current_revision} to the field?`)
              && run(() => issuePlan(planId), (r) => `Issued v${r.issued.rev_no}.`)}>
            Issue
          </button>
        )}
      </div>
      {done && <p className="op-meta" role="status">{done}</p>}
      <Refusal error={error} testid="pp-lifecycle-error" />
    </div>
  );
}

/* ── the versions strip ────────────────────────────────────────────────── */

/**
 * In the plan page's head, under the stepper (which version is shown is the
 * page's state, not the map's). A version is a button that wraps rather than
 * clips; past VERSIONS_SHOWN the older ones are a menu, and the current, the
 * last approved and the viewed one are always buttons (splitVersions).
 */
export function VersionsStrip({ approval, viewedRevNo, onView }) {
  const rows = versionRows(approval);
  if (rows.length === 0) return null;
  const { shown, folded } = splitVersions(rows, viewedRevNo);
  const label = (v) => `v${v.rev_no}${v.date ? ` · ${shortDate(v.date)}` : ""} (${v.word}${v.current ? ", current" : ""})`;
  return (
    <section className="flex flex-col gap-1" aria-label="Versions" data-testid="pp-versions">
      <SectionLabel>Versions</SectionLabel>
      <div className="flex flex-wrap items-center gap-1.5">
        {shown.map((v) => (
          <button key={v.rev_no} type="button" aria-pressed={v.rev_no === viewedRevNo} data-testid="pp-version" data-rev={v.rev_no}
            onClick={() => onView(v.rev_no)}
            className={`flex max-w-full flex-wrap items-center gap-x-1.5 rounded border px-2 py-0.5 text-left text-[12.5px] ${v.rev_no === viewedRevNo ? "border-accent-action/60 bg-accent-action/15 text-accent-action" : "border-bg-border text-fg-secondary hover:text-fg-primary"}`}>
            <span aria-hidden="true">{v.rev_no === viewedRevNo ? "●" : "○"}</span>
            <span>{label(v)}</span>
            {v.lastApproved && <span className="op-chip px-1 py-0 text-status-ok" data-testid="pp-last-approved">Last approved</span>}
          </button>
        ))}
        {folded.length > 0 && (
          <select className="rounded border border-bg-border bg-bg px-1.5 py-0.5 text-[12.5px] text-fg-secondary" aria-label="Older versions"
            data-testid="pp-older-versions" value="" onChange={(e) => e.target.value && onView(Number(e.target.value))}>
            <option value="">Older versions ({folded.length})</option>
            {folded.map((v) => <option key={v.rev_no} value={v.rev_no}>{label(v)}</option>)}
          </select>
        )}
      </div>
    </section>
  );
}

/* ── comments ──────────────────────────────────────────────────────────── */

/**
 * A revision's comments, by pin. Adding one takes a place: the next map
 * click pins it there (and names the feature clicked, if any). The author
 * edits an open comment; the author or an org_admin resolves it, once; a
 * resolved comment is read-only. Any revision, a frozen one included.
 */
export function Comments({ planId, revNo, list, selectedId, onSelect, pinning, onPinning, place, onClearPlace, onChanged, loadError = null, onRetry = null }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const comments = list?.comments ?? [];
  const canComment = list?.can_comment === true;

  async function act(fn) {
    setBusy(true);
    setError(null);
    try { await fn(); await onChanged?.(); return true; } catch (err) { setError(err); return false; } finally { setBusy(false); }
  }

  async function add() {
    const ok = await act(() => addComment(planId, revNo, {
      body, geometry: { type: "Point", coordinates: place.lngLat }, ...(place.featureId ? { feature_id: place.featureId } : {}),
    }));
    if (ok) { setBody(""); onClearPlace(); }
  }

  return (
    <section className="flex flex-col gap-2" aria-label="Comments" data-testid="pp-comments">
      <SectionLabel>Comments{list ? ` · ${comments.length}` : ""}</SectionLabel>
      {loadError && <ReadFailed what="the comments" error={loadError} onRetry={onRetry} testid="pp-comments-failed" />}
      {!list && !loadError && <p className="op-meta">Loading comments…</p>}
      <ul className="flex flex-col gap-2">
        {comments.map((c) => (
          <li key={c.id} data-testid="pp-comment" data-comment-id={c.id} data-resolved={c.resolved ? "true" : "false"}
            className={`flex gap-2 rounded px-1 py-1 ${c.id === selectedId ? "bg-accent-action/10" : ""}`}>
            <button type="button" onClick={() => onSelect(c.id)} aria-label={`Show comment ${c.pin_no} on the map`}
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${c.resolved ? "bg-slate-500 text-slate-100" : "bg-orange-500 text-gray-900"}`}>
              {c.pin_no}
            </button>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <p className="op-meta"><span className="font-semibold text-fg-primary">{c.author.display_name}</span> · {shortDate(c.created_at)}</p>
              {editing?.id === c.id ? (
                <div className="flex flex-col gap-1">
                  <textarea className={field} rows={2} value={editing.body} onChange={(e) => setEditing({ id: c.id, body: e.target.value })} aria-label="Edit comment" />
                  <div className="flex gap-2">
                    <button type="button" className="op-btn op-btn--primary op-btn--sm" disabled={busy}
                      onClick={async () => { if (await act(() => editComment(planId, c.id, editing.body))) setEditing(null); }}>Save</button>
                    <button type="button" className="op-btn op-btn--sm" onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <p className={`whitespace-pre-wrap text-[13px] ${c.resolved ? "text-fg-secondary" : "text-fg-primary"}`} data-testid="pp-comment-body">{c.body}</p>
              )}
              {c.resolved && <p className="op-meta" data-testid="pp-comment-resolved">Resolved{c.resolved.by_display_name ? ` by ${c.resolved.by_display_name}` : ""} · {shortDate(c.resolved.at)}</p>}
              {!c.resolved && editing?.id !== c.id && (c.can_edit || c.can_resolve) && (
                <div className="flex gap-3">
                  {c.can_edit && <button type="button" className="op-meta hover:text-fg-primary" onClick={() => setEditing({ id: c.id, body: c.body })}>Edit</button>}
                  {c.can_resolve && <button type="button" className="op-meta hover:text-fg-primary" disabled={busy}
                    onClick={() => act(() => resolveComment(planId, c.id))}>Resolve</button>}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
      {canComment && (
        <div className="flex flex-col gap-1.5" data-testid="pp-comment-new">
          <textarea className={field} rows={2} maxLength={4000} value={body} onChange={(e) => setBody(e.target.value)}
            placeholder="Comment, or click the map to pin one" aria-label="New comment" />
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="op-btn op-btn--sm" aria-pressed={pinning} onClick={() => onPinning(!pinning)} data-testid="pp-pin-toggle">
              {pinning ? "Click the map…" : place ? "Move pin" : "Pin on map"}
            </button>
            {place && <span className="op-meta" data-testid="pp-pin-place">{place.featureName ? `On ${place.featureName}` : "Pinned on the map"}</span>}
            <button type="button" className="op-btn op-btn--primary op-btn--sm ml-auto" disabled={busy || !body.trim() || !place} onClick={add}
              data-testid="pp-comment-add">Add comment</button>
          </div>
          {!place && body.trim() && <p className="op-meta">Pin it on the map to add it.</p>}
        </div>
      )}
      <Refusal error={error} testid="pp-comment-error" />
    </section>
  );
}
